import { AsyncPipe, NgIf } from '@angular/common';
import { ChangeDetectionStrategy, Component, OnDestroy } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { showErrorSwal } from '@asamco/utils';
import { Subject } from '@microsoft/signalr';
import { DxCheckBoxModule, DxDataGridModule, DxSelectBoxModule, DxTextBoxModule } from 'devextreme-angular';
import { DxTemplateModule } from 'devextreme-angular/core';
import { DxiColumnModule, DxoPagingModule, DxoSearchPanelModule, DxoSortingModule } from 'devextreme-angular/ui/nested';
import CustomStore from 'devextreme/data/custom_store';
import DataSource from 'devextreme/data/data_source';
import { ColumnButtonClickEvent } from 'devextreme/ui/data_grid';
import { Observable, catchError, combineLatest, firstValueFrom, map, of, switchMap, throwError } from 'rxjs';
import { CompanyType } from '../../../enums/company-type.enum';
import { AlCompanyRequestV2Service, CompanyRequestDto } from '../../../services/al-company-request-v2.service';
import { CompanyDto } from '../../../services/al-company-v2.service';
import { AdminWarningBannerComponent } from '../admin-warning-banner.component';

type ViewModel = {
	state: 'loading'
} | {
	state: 'ready'
	dataSource: DataSource;
	companyDatasource: CompanyDto[];
	userDatasource: unknown[];
} | {
	state: 'error'
}

@Component({
	selector: 'app-shell-admin-company-request-management-grid',
	templateUrl: './admin-company-request-management-grid.component.html',
	styleUrls: ['./admin-company-request-management-grid.component.scss'],
	changeDetection: ChangeDetectionStrategy.OnPush,
	standalone: true,
	imports: [
		FormsModule,
		NgIf,
		ReactiveFormsModule,
		DxTextBoxModule,
		DxCheckBoxModule,
		DxSelectBoxModule,
		DxDataGridModule,
		DxoSortingModule,
		DxoPagingModule,
		DxoSearchPanelModule,
		DxiColumnModule,
		DxTemplateModule,
		AsyncPipe,
		AdminWarningBannerComponent,
	],
})

export class AdminCompanyRequestManagementGridComponent implements OnDestroy {
	private readonly destroy$ = new Subject<void>();

	public readonly viewModel$: Observable<ViewModel> = of({
		state: 'loading'
	}).pipe(
		switchMap(() => combineLatest([
			this.companyRequestService.getAllPotentialCompanies(),
			this.companyRequestService.getAllPotentialUsers(),
		])),
		map(([potentialCompanies, potentialUsers]) => {
			const viewModel: ViewModel = {
				state: 'ready',
				dataSource: this.buildDataSource(),
				userDatasource: potentialUsers,
				companyDatasource: potentialCompanies,
			};
			return viewModel;
		}),
		catchError(error => {
			showErrorSwal(error);
			const viewModel: ViewModel = {
				state: 'error'
			};
			return of(viewModel);
		}),
	);

	constructor(
		private companyRequestService: AlCompanyRequestV2Service,
	) {

	}

	ngOnDestroy(): void {
		this.destroy$.next();
		this.destroy$.complete();
	}

	public getCompanyEnumValue(companyTypeValue: number) {
		return CompanyType[companyTypeValue];
	}

	public companyIdDisplayExpr(item: CompanyDto) {
		if (item?.name && item?.fullIdentifier) {
			return `${item.name} (${item.fullIdentifier})`;
		}
		return '(empty)';
	}

	public approve(data: ColumnButtonClickEvent) {
		if (data?.row?.data) {
			this.companyRequestService.approve(data.row.data).subscribe({
				next: () => data.component.refresh(),
				error: error => showErrorSwal(error),
			});
		}
	}

	public reject(data: ColumnButtonClickEvent) {
		if (data?.row?.data) {
			this.companyRequestService.reject(data.row.data).subscribe({
				next: () => data.component.refresh(),
				error: error => showErrorSwal(error),
			});
		}
	}

	private buildDataSource(): DataSource {
		return new DataSource<CompanyRequestDto, CompanyRequestDto>({
			key: 'id',
			store: new CustomStore({
				// key: 'id',
				load: _loadOptions => {
					return firstValueFrom(this.companyRequestService.getAll().pipe(this.handleDataStoreError()));
				},
				byKey: (key: CompanyRequestDto) => {
					return firstValueFrom(this.companyRequestService.get(key.id).pipe(this.handleDataStoreError()));
				},
				update: (key: CompanyRequestDto, values: CompanyRequestDto) => {
					// TODO: DevExtreme expects to only pass changed values. I don't want to keep track of that data
					// so that we can do a PUT. So just don't define a key, and pass the whole object as 'key'.
					const newValues =  Object.assign(key, values);
					// if (key !== values.id) {
					// 	throw new Error('ID is not the same when updating company.');
					// }
					return firstValueFrom(this.companyRequestService.put(newValues).pipe(this.handleDataStoreError()));
				},
			}),
		});
	}

	private handleDataStoreError<T>(): (source: Observable<T>) => Observable<T> {
		return (source: Observable<T>): Observable<T> =>
			source.pipe(
				catchError(error => {
					showErrorSwal(error)
					// Let Dx do its thing as well.
					return throwError(() => error);
				})
			);
	}
}
